'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowRight } from '@/components/ui/icons'
import { useExtracted } from 'next-intl'
import { trackPricingPageViewed } from '@/lib/analytics'
import { useAuth } from '@/hooks/useAuth'
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess'
import { SignUpModal } from '@/components/auth/SignUpModal'

// Same shape as the homepage's FinalCtaSection: one line, one button, no box.
export function PricingCTA() {
  const t = useExtracted()
  const router = useRouter()
  const hasTrackedPageView = useRef(false)
  const { auth, isLoading: authLoading, isAnonymous } = useAuth()
  const hasSignupAccess = useStoredSignupAccess()
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  // Match PlanCards: signed-in users keep today's CTA even if a stored
  // code lingers in localStorage (e.g. a promo link visited while logged in).
  const isRealAccount = !!auth?.userId && !isAnonymous

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPricingPageViewed()
      hasTrackedPageView.current = true
    }
  }, [])

  const ctaClassName =
    'group/btn inline-flex h-14 items-center justify-center gap-2.5 rounded-xl bg-white px-8 text-[17px] font-semibold text-black transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-50'

  return (
    <div className="flex flex-col items-center text-center">
      <h2 className="max-w-[1120px] text-[40px] font-semibold leading-[1.05] tracking-[-0.035em] md:text-[64px]">
        Make something beautiful with Martini
      </h2>
      <p className="mt-6 max-w-[560px] text-[17px] text-white/70 md:text-[19px]">
        Free to start. Every model on every plan.
      </p>
      <div className="mt-12">
        {/* While auth initializes, a stored code makes the right CTA
            ambiguous — hold like the plan cards do instead of letting a
            quick click land an eligible visitor on the waitlist. */}
        {hasSignupAccess && authLoading ? (
          <button type="button" className={ctaClassName} disabled>
            {t('Loading...')}
          </button>
        ) : hasSignupAccess && !isRealAccount ? (
          <button
            type="button"
            data-cta="start_for_free"
            onClick={() => setShowSignUpModal(true)}
            className={ctaClassName}
          >
            Get started
            <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
          </button>
        ) : (
          <a
            href="https://form.typeform.com/to/VVwyeUrj"
            target="_blank"
            rel="noopener noreferrer"
            data-cta="start_for_free"
            className={ctaClassName}
          >
            Get started
            <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
          </a>
        )}
      </div>

      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        onSuccess={() => router.push('/select-plan')}
        returnPath="/select-plan"
        defaultMode="signup"
        variant="marketing"
        source="pricing_cta"
      />
    </div>
  )
}
